import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, AcademicCapIcon, ChartBarIcon, SparklesIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import studiesService, { SubjectStatistics, ModulesStatistics } from '../services/studies';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

interface SubjectStatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubjectStatisticsModal: React.FC<SubjectStatisticsModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<SubjectStatistics[]>([]);
  const [modulesStats, setModulesStats] = useState<ModulesStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadStatistics();
    }
  }, [isOpen]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const [subjectData, modulesData] = await Promise.all([
        studiesService.getStatisticsBySubject(),
        studiesService.getModulesStatistics(),
      ]);
      setStatistics(subjectData);
      setModulesStats(modulesData);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Falha ao carregar estatísticas por assunto');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 backdrop-blur-sm"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 via-cyan-600 to-blue-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white dark:bg-white/10 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                  <ChartBarIcon className="h-7 w-7 text-teal-600 dark:text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white drop-shadow-md">
                  Estatísticas por Assunto
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white/20 rounded-lg"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[70vh] overflow-y-auto bg-gray-50 dark:bg-gray-900">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {modulesStats && (
                  <div className="mb-6 bg-gradient-to-br from-purple-500 to-indigo-600 dark:from-purple-600 dark:to-indigo-700 rounded-xl p-5 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                          <SparklesIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="text-white font-bold">Módulos com IA</h4>
                          <p className="text-purple-100 text-xs">
                            {modulesStats.completedModules} de {modulesStats.totalModules} módulos concluídos
                          </p>
                        </div>
                      </div>
                      {!modulesStats.aiModulesUnlimited && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-white">
                            {modulesStats.aiGeneratedModules}/{modulesStats.aiModulesLimit}
                          </div>
                          <div className="text-purple-100 text-xs font-medium">Gerados com IA</div>
                        </div>
                      )}
                    </div>

                    {modulesStats.aiModulesUnlimited ? (
                      <p className="text-purple-100 text-sm">
                        Você tem geração ilimitada de conteúdo com IA para seus módulos. ✨
                      </p>
                    ) : (
                      <>
                        <div className="w-full bg-white/20 rounded-full h-2.5 overflow-hidden mb-3">
                          <div
                            className="bg-white h-2.5 rounded-full transition-all duration-500"
                            style={{
                              width: `${Math.min(
                                (modulesStats.aiGeneratedModules / Math.max(modulesStats.aiModulesLimit, 1)) * 100,
                                100
                              )}%`,
                            }}
                          ></div>
                        </div>
                        {modulesStats.aiModulesRemaining === 0 ? (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <p className="text-purple-100 text-sm">
                              Você atingiu o limite gratuito de módulos gerados com IA.
                            </p>
                            <button
                              onClick={() => navigate('/pricing')}
                              className="inline-flex items-center justify-center px-4 py-2 bg-white text-purple-700 text-sm font-semibold rounded-lg hover:bg-purple-50 transition-colors"
                            >
                              Fazer Upgrade
                            </button>
                          </div>
                        ) : (
                          <p className="text-purple-100 text-sm">
                            Restam {modulesStats.aiModulesRemaining} módulo(s) gratuito(s) com IA.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {statistics.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <AcademicCapIcon className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className="text-gray-700 dark:text-gray-200 text-lg font-semibold mb-2">
                  Nenhum estudo encontrado
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Crie estudos e adicione assuntos para visualizar estatísticas aqui
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {statistics.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow duration-200"
                  >
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 dark:from-teal-400 dark:to-cyan-500 rounded-xl flex items-center justify-center mr-3 shadow-md">
                        <AcademicCapIcon className="h-7 w-7 text-white" />
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {stat.subject || 'Sem Assunto'}
                      </h4>
                    </div>

                    {/* AI Modules feature: per-subject module progress */}
                    {stat.modulesTotal > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <RectangleStackIcon className="h-4 w-4 mr-1.5 text-purple-500" />
                          <span>
                            {stat.modulesCompleted}/{stat.modulesTotal} módulos concluídos
                          </span>
                        </div>
                        {stat.modulesWithAiContent > 0 && (
                          <div className="flex items-center text-xs text-purple-600 dark:text-purple-400 font-medium">
                            <SparklesIcon className="h-3.5 w-3.5 mr-1" />
                            {stat.modulesWithAiContent} com IA
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 dark:from-teal-400 dark:to-cyan-500 dark:hover:from-teal-500 dark:hover:to-cyan-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectStatisticsModal;
